import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { LogoHorizontal } from "@/components/Logo";
import StickyMobileCTA from "@/components/StickyMobileCTA";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/comment-ca-marche", label: "Comment ça marche", nowrap: true },
  { to: "/annonces", label: "Annonces" },
  { to: "/simulateur", label: "Simulateur" },
  { to: "/estimer", label: "Estimer" },
  { to: "/blog", label: "Guides" },
  { to: "/deposer-annonce", label: "Déposer" },
  { to: "/agences", label: "Agences", nowrap: true },
  { to: "/contact", label: "Contact" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-cream">
      <a href="#main-content" className="skip-link" data-testid="skip-link">Aller au contenu principal</a>
      <header data-testid="site-header" className="sticky top-0 z-40 backdrop-blur-xl bg-[#F7F5F0]/85 border-b border-navy/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-4 flex items-center justify-between">
          <Link to="/" data-testid="brand-link" className="flex items-center group">
            <LogoHorizontal height={44} />
          </Link>
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide hover:text-accent-brand transition-colors ${n.nowrap ? "whitespace-nowrap" : ""} ${isActive ? "text-accent-brand" : "text-navy"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <button className="lg:hidden p-2 text-navy" data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-navy/10 bg-cream">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} data-testid={`mobile-nav-${n.to.replace(/\//g, "") || "home"}`}
                  className="py-2 text-navy hover:text-accent-brand">{n.label}</Link>
              ))}
            </div>
          </div>
        )}
      </header>

      <main id="main-content" className="flex-1" tabIndex={-1}>
        <Outlet />
      </main>

      <StickyMobileCTA />

      <footer data-testid="site-footer" className="bg-navy text-cream mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-12">
          <div className="md:col-span-2">
            <div className="mb-4"><LogoHorizontal height={52} darkMode /></div>
            <p className="text-sm text-cream/70 max-w-md leading-relaxed">
              La vente à terme immobilière — le crédit vendeur en toute sécurité. Une alternative moderne au viager, sans aléa de durée de vie. Le régime fiscal dépend de votre situation ; à valider avec votre notaire.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-accent-brand mb-4 font-bold">Navigation</div>
            <ul className="space-y-2 text-sm text-cream/80">
              {NAV.map((n) => (<li key={n.to}><Link to={n.to} className="hover:text-accent-brand">{n.label}</Link></li>))}
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-accent-brand mb-4 font-bold">La vente à terme</div>
            <ul className="space-y-2 text-sm text-cream/80">
              <li><Link to="/vendre-en-vente-a-terme" className="hover:text-accent-brand">Vendre en vente à terme</Link></li>
              <li><Link to="/avantages-inconvenients" className="hover:text-accent-brand">Avantages &amp; inconvénients</Link></li>
              <li><Link to="/notre-philosophie" className="hover:text-accent-brand">Notre philosophie</Link></li>
              <li><Link to="/ventes-realisees" className="hover:text-accent-brand">Ventes réalisées</Link></li>
              <li><Link to="/revue-de-presse" className="hover:text-accent-brand">Revue de presse</Link></li>
              <li><Link to="/estimer" className="hover:text-accent-brand">Estimer mon bien</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-accent-brand mb-4 font-bold">Légal</div>
            <ul className="space-y-2 text-sm text-cream/80">
              <li><Link to="/contact" className="hover:text-accent-brand">Contact</Link></li>
              <li><Link to="/deposer-annonce" className="hover:text-accent-brand">Déposer une annonce</Link></li>
              <li><Link to="/agences" className="hover:text-accent-brand">Espace agences</Link></li>
              <li><Link to="/alertes" className="hover:text-accent-brand">Créer une alerte</Link></li>
              <li><Link to="/mentions-legales" className="hover:text-accent-brand">Mentions légales</Link></li>
              <li><Link to="/cgu" className="hover:text-accent-brand">CGU</Link></li>
              <li><Link to="/confidentialite" className="hover:text-accent-brand">Confidentialité (RGPD)</Link></li>
              <li><Link to="/login" data-testid="footer-admin-link" className="hover:text-accent-brand">Espace administrateur</Link></li>
              <li className="pt-3 border-t border-cream/10 mt-3">
                <a
                  href={process.env.REACT_APP_SUPPORT_URL || "#"}
                  data-testid="footer-support-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cream/70 hover:text-accent-brand text-xs uppercase tracking-[0.2em]"
                >
                  Soutenir le projet
                </a>
              </li>
              <li className="text-xs text-cream/50 pt-4">© {new Date().getFullYear()} venteaterme.fr — Sabra Bouilfane · SIREN 990&nbsp;010&nbsp;217 · RCS Marseille · 45 rue de Crimée, 13003 Marseille · Plateforme 100&nbsp;% gratuite. Aucune commission. Transactions conclues chez notaire.</li>
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
}
